import type { AORHierarchyNode } from '../AorsTab/utils/schema';
import {
  buildAorTree,
  collectDescendantIds,
} from '../AorsTab/utils/buildAorTree';
import { eventMatchesAorNames } from '../EventsTab/eventsListFilters';

/** The selected AOR plus every AOR beneath it. */
export type SitStatAorScope = {
  selectedAorId: string;
  selectedAorName: string | null;
  ids: string[];
  names: string[];
};

/**
 * Resolves a selected AOR to its subtree. Before the hierarchy loads (or for an
 * id it does not contain) the scope is just the selected id, so widgets stay
 * narrowed instead of briefly showing every AOR's data.
 */
export function resolveSitStatAorScope(
  nodes: AORHierarchyNode[] | undefined,
  selectedAorId: string | null
): SitStatAorScope | null {
  if (!selectedAorId) return null;
  const { childrenByParent, nodesById } = buildAorTree(nodes ?? []);
  const selected = nodesById[selectedAorId];
  if (!selected) {
    return {
      selectedAorId,
      selectedAorName: null,
      ids: [selectedAorId],
      names: [],
    };
  }
  const ids = [
    selectedAorId,
    ...collectDescendantIds(childrenByParent, selectedAorId),
  ];
  return {
    selectedAorId,
    selectedAorName: selected.name,
    ids,
    names: ids.map((id) => nodesById[id]?.name).filter(Boolean) as string[],
  };
}

export function scopeIncludesAor(
  scope: SitStatAorScope | null,
  aorId: string | null | undefined
): boolean {
  if (!scope) return true;
  return !!aorId && scope.ids.includes(String(aorId));
}

export function scopeIncludesAnyAor(
  scope: SitStatAorScope | null,
  aorIds: readonly (string | null | undefined)[] | null | undefined
): boolean {
  if (!scope) return true;
  return (aorIds ?? []).some((id) => scopeIncludesAor(scope, id));
}

/** Events carry AORs as region names rather than ids. */
export function scopeIncludesRegion(
  scope: SitStatAorScope | null,
  region: string | null | undefined
): boolean {
  if (!scope) return true;
  if (scope.names.length === 0) return false;
  return eventMatchesAorNames({ region }, scope.names);
}

/** Hierarchy order with a depth for indenting the AOR picker. */
export function flattenAorHierarchy(
  nodes: AORHierarchyNode[] | undefined
): { node: AORHierarchyNode; depth: number }[] {
  const { childrenByParent, nodesById } = buildAorTree(nodes ?? []);
  const roots = (nodes ?? []).filter(
    (node) =>
      !node.parentItemId ||
      node.parentItemId === node.id ||
      !nodesById[node.parentItemId]
  );
  const out: { node: AORHierarchyNode; depth: number }[] = [];
  const seen = new Set<string>();
  const visit = (node: AORHierarchyNode, depth: number) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    out.push({ node, depth });
    (childrenByParent[node.id] ?? []).forEach((child) =>
      visit(child, depth + 1)
    );
  };
  roots.forEach((root) => visit(root, 0));
  return out;
}
